import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const ProfileQueueContext = createContext();

const STORAGE_KEY = "isugar-profile-queue";

export const useProfileQueue = () => {
  const context = useContext(ProfileQueueContext);
  if (!context) {
    throw new Error("useProfileQueue deve ser usado dentro de um ProfileQueueProvider");
  }
  return context;
};

const readStoredQueue = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Guarda a lista ordenada de perfis (com foto/nome) que o usuário está
// navegando (carrossel, busca, matches, feed...) para permitir avançar/voltar
// entre perfis — com uma tira de avatares tocáveis — sem precisar retornar à
// tela de origem a cada clique.
export const ProfileQueueProvider = ({ children }) => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState(readStoredQueue);

  const persist = useCallback((next) => {
    setQueue(next);
    try {
      if (next) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // sessionStorage indisponível (modo privado, quota) — segue só em memória
    }
  }, []);

  // Chamado pelos cards/listas antes de navegar para `/dash/profile-details`.
  // `items` carrega foto/nome para poder desenhar a tira de avatares sem
  // precisar buscar cada perfil de novo na API.
  const openQueue = useCallback(
    ({ items, currentId, sourceLabel, returnPath }) => {
      const seen = new Set();
      const cleanItems = (items || [])
        .filter((it) => it && it.id != null)
        .map((it) => ({
          id: String(it.id),
          photo: it.photo || null,
          name: it.name || null,
        }))
        .filter((it) => {
          if (seen.has(it.id)) return false;
          seen.add(it.id);
          return true;
        });
      const index = Math.max(
        cleanItems.findIndex((it) => it.id === String(currentId)),
        0,
      );
      persist({
        items: cleanItems,
        index,
        sourceLabel: sourceLabel || null,
        returnPath: returnPath || null,
      });
    },
    [persist],
  );

  const goToIndex = useCallback(
    (index) => {
      if (!queue || index < 0 || index >= queue.items.length) return;
      persist({ ...queue, index });
      navigate(`/dash/profile-details?userUid=${queue.items[index].id}`);
    },
    [queue, persist, navigate],
  );

  const goNext = useCallback(() => goToIndex((queue?.index ?? 0) + 1), [queue, goToIndex]);
  const goPrev = useCallback(() => goToIndex((queue?.index ?? 0) - 1), [queue, goToIndex]);
  const closeQueue = useCallback(() => persist(null), [persist]);

  const value = useMemo(() => {
    const items = queue?.items ?? [];
    const total = items.length;
    return {
      items,
      index: queue?.index ?? -1,
      openQueue,
      goNext,
      goPrev,
      goToIndex,
      closeQueue,
      total,
      hasNext: !!queue && queue.index < total - 1,
      hasPrev: !!queue && queue.index > 0,
      position: queue ? queue.index + 1 : 0,
      sourceLabel: queue?.sourceLabel ?? null,
      returnPath: queue?.returnPath ?? null,
      // Índice do perfil atualmente aberto dentro da fila — usado pela tela de
      // perfil para decidir se mostra a tira de avatares (só faz sentido se o
      // perfil aberto realmente pertence à fila guardada).
      indexOf: (userUid) => items.findIndex((it) => it.id === String(userUid)),
    };
  }, [queue, openQueue, goNext, goPrev, goToIndex, closeQueue]);

  return (
    <ProfileQueueContext.Provider value={value}>
      {children}
    </ProfileQueueContext.Provider>
  );
};
